/**
 * Client Store
 * Manages client state
 */

import { create } from 'zustand';
import {
  createClient,
  updateClient,
  deleteClient,
  getClient,
  subscribeToClients,
  createPortalAccess,
  getPortalAccess,
  disablePortalAccess,
  regeneratePortalToken,
} from '../services/clientService';

export const useClientStore = create((set, get) => ({
  clients: [],
  loading: false,
  error: null,

  // Subscribe to clients
  subscribeToClients: (userId, isAdmin = false) => {
    return subscribeToClients(userId, (clients) => {
      set({ clients });
    }, isAdmin);
  },

  // Create new client
  createClient: async (userId, clientData) => {
    try {
      set({ loading: true, error: null });
      const result = await createClient(userId, clientData);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Update client
  updateClient: async (clientId, clientData) => {
    try {
      set({ loading: true, error: null });
      const result = await updateClient(clientId, clientData);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Delete client
  deleteClient: async (clientId) => {
    try {
      set({ loading: true, error: null });
      const result = await deleteClient(clientId);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Get client by ID
  getClientById: (clientId) => {
    return get().clients.find(c => c.id === clientId);
  },

  // Clear error
  clearError: () => set({ error: null }),

  // Portal Access Management
  createPortalAccess: async (clientId, permissions = {}) => {
    try {
      set({ loading: true, error: null });
      const result = await createPortalAccess(clientId, permissions);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  getPortalAccess: async (clientId) => {
    try {
      const result = await getPortalAccess(clientId);
      return result;
    } catch (error) {
      console.error('Error getting portal access:', error);
      throw error;
    }
  },

  disablePortalAccess: async (clientId) => {
    try {
      set({ loading: true, error: null });
      const result = await disablePortalAccess(clientId);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  regeneratePortalToken: async (clientId) => {
    try {
      set({ loading: true, error: null });
      const result = await regeneratePortalToken(clientId);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },
}));
